import {
  eventContentSchema,
  listContentSchema,
  listItemContentSchema,
  messageContentSchema,
  topicContentSchema,
  wishContentSchema,
  type ContentBlob,
} from "@vmeste/shared";
import { t } from "../i18n/index.js";
import type { SpaceState } from "./store.js";
import { plaintextOf } from "./plaintext.js";

/**
 * Searching and summarising, on the device.
 *
 * Neither of these asks the server, and that is a design decision rather than a
 * shortcut: in an encrypted space the server holds ciphertext, so a server-side
 * search cannot exist. Building one for plaintext spaces and a different one for
 * encrypted spaces would be the same feature twice — which this project has
 * already had to undo once, with wishes.
 *
 * The client holds every row it is allowed to see, so both of these are a pass
 * over state it already has.
 */

/** Decode a blob for reading. Returns null for anything this device cannot read. */
function read<T>(blob: ContentBlob | null, schema: { safeParse: (v: unknown) => { success: boolean; data?: T } }): T | null {
  const value = plaintextOf(blob);
  if (value === undefined) return null;
  const parsed = schema.safeParse(value);
  return parsed.success ? (parsed.data as T) : null;
}

export type SearchKind = "list" | "item" | "wish" | "message" | "event" | "topic";

export interface SearchHit {
  kind: SearchKind;
  id: string;
  title: string;
  subtitle?: string;
  snippet?: string;
  at: string;
  href: string;
}

/**
 * Case- and accent-insensitive, and й/ё-insensitive.
 *
 * Somebody typing "елка" should find "Ёлка": the two letters are the same
 * sound and nobody reaches for the ё key on a phone. `localeCompare` cannot do
 * substring matching, so this normalises both sides instead.
 */
export function normalise(value: string): string {
  return value
    .toLowerCase()
    .replaceAll("ё", "е")
    .replaceAll("й", "и")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "");
}

/** The matched text with a little context around it. */
export function snippetAround(text: string, query: string, radius = 40): string | undefined {
  const at = normalise(text).indexOf(normalise(query));
  if (at === -1) return undefined;

  const start = Math.max(0, at - radius);
  const end = Math.min(text.length, at + query.length + radius);

  return `${start > 0 ? "…" : ""}${text.slice(start, end)}${end < text.length ? "…" : ""}`;
}

const matches = (haystack: string | undefined, needle: string): boolean =>
  haystack !== undefined && normalise(haystack).includes(needle);

/**
 * Everything in the space matching a query, most recent first.
 *
 * An empty query returns nothing rather than everything: a search box that
 * dumps the whole space the moment it is focused is noise, not a feature.
 */
export function searchSpace(state: SpaceState, query: string, limit = 50): SearchHit[] {
  const needle = normalise(query.trim());
  if (needle.length === 0) return [];

  const hits: SearchHit[] = [];

  for (const list of Object.values(state.lists)) {
    const content = read(list.content, listContentSchema);
    if (!content) continue;

    if (matches(content.title, needle)) {
      hits.push({
        kind: "list",
        id: list.id,
        title: content.title,
        at: list.updatedAt,
        href: `/lists?list=${list.id}`,
      });
    }
  }

  for (const item of Object.values(state.items)) {
    const content = read(item.content, listItemContentSchema);
    if (!content) continue;

    const listTitle = read(state.lists[item.listId]?.content ?? null, listContentSchema);
    const hit = matches(content.text, needle) ? content.text : content.note;

    if (matches(content.text, needle) || matches(content.note, needle)) {
      hits.push({
        kind: "item",
        id: item.id,
        title: content.text,
        ...(listTitle ? { subtitle: listTitle.title } : {}),
        ...(hit && hit !== content.text
          ? { snippet: snippetAround(hit, query) ?? "" }
          : {}),
        at: item.updatedAt,
        href: `/lists?list=${item.listId}&item=${item.id}`,
      });
    }
  }

  for (const wish of Object.values(state.wishes)) {
    const content = read(wish.content, wishContentSchema);
    if (!content) continue;

    if (
      matches(content.title, needle) ||
      matches(content.note, needle) ||
      matches(content.preview?.title, needle)
    ) {
      hits.push({
        kind: "wish",
        id: wish.id,
        title: content.title,
        ...(content.url ? { subtitle: hostOf(content.url) } : {}),
        ...(content.note && matches(content.note, needle)
          ? { snippet: snippetAround(content.note, query) ?? "" }
          : {}),
        at: wish.updatedAt,
        href: `/wishes?wish=${wish.id}`,
      });
    }
  }

  for (const message of Object.values(state.messages)) {
    const content = read(message.content, messageContentSchema);
    if (!content || !matches(content.body, needle)) continue;

    const topic = read(state.topics[message.topicId]?.content ?? null, topicContentSchema);

    hits.push({
      kind: "message",
      id: message.id,
      title: content.body.slice(0, 120),
      subtitle: topic?.title ?? t("chat.general"),
      // The conversation it was said in, which is as close as this can get:
      // there is no scroll-to-message, and for two people a thread is short.
      ...(snippetAround(content.body, query)
        ? { snippet: snippetAround(content.body, query)! }
        : {}),
      at: message.createdAt,
      href: `/chat?topic=${message.topicId}`,
    });
  }

  for (const event of Object.values(state.events)) {
    const content = read(event.content, eventContentSchema);
    if (!content) continue;

    if (
      matches(content.title, needle) ||
      matches(content.note, needle) ||
      matches(content.location, needle)
    ) {
      hits.push({
        kind: "event",
        id: event.id,
        title: content.title,
        ...(content.location ? { subtitle: content.location } : {}),
        at: event.startsAt,
        href: `/calendar?event=${event.id}`,
      });
    }
  }

  for (const topic of Object.values(state.topics)) {
    const content = read(topic.content, topicContentSchema);
    if (!content || !matches(content.title, needle)) continue;

    hits.push({
      kind: "topic",
      id: topic.id,
      title: content.title,
      at: topic.updatedAt,
      href: `/chat?topic=${topic.id}`,
    });
  }

  return hits.sort((a, b) => (a.at < b.at ? 1 : -1)).slice(0, limit);
}

function hostOf(url: string): string | undefined {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

/* --------------------------------------------------------------- activity */

export interface ActivityEntry {
  id: string;
  kind: SearchKind;
  verb: "created" | "updated" | "completed" | "deleted";
  title: string;
  actorId: string | null;
  at: string;
  href: string;
}

/**
 * What has happened lately, newest first.
 *
 * Derived from the rows themselves rather than from an events table: every row
 * already carries who made it and when it last changed, so a separate log would
 * be a second copy of the same facts that could disagree with the first.
 *
 * The cost is honest and worth stating: this can only say *that* something
 * changed, not what it was before. A row knows its author and its timestamps,
 * not its history.
 */
export function recentActivity(state: SpaceState, limit = 50): ActivityEntry[] {
  const entries: ActivityEntry[] = [];

  // `at` is derived from `updated`, and `verb` from the pair, so neither is
  // asked for at the call site.
  const push = (
    entry: Omit<ActivityEntry, "verb" | "at"> & {
      created: string;
      updated: string;
      verb?: ActivityEntry["verb"];
    },
  ) => {
    entries.push({
      id: entry.id,
      kind: entry.kind,
      // Within a second of each other means nothing has happened since; treat
      // that as a creation rather than claiming an edit nobody made.
      verb:
        entry.verb ??
        (Math.abs(Date.parse(entry.updated) - Date.parse(entry.created)) < 1_000
          ? "created"
          : "updated"),
      title: entry.title,
      actorId: entry.actorId,
      at: entry.updated,
      href: entry.href,
    });
  };

  for (const item of Object.values(state.items)) {
    const content = read(item.content, listItemContentSchema);
    if (!content) continue;

    push({
      id: item.id,
      kind: "item",
      title: content.text,
      actorId: item.done ? item.doneBy : item.createdBy,
      created: item.createdAt,
      updated: item.updatedAt,
      href: `/lists?list=${item.listId}&item=${item.id}`,
      ...(item.done ? { verb: "completed" as const } : {}),
    });
  }

  for (const wish of Object.values(state.wishes)) {
    const content = read(wish.content, wishContentSchema);
    if (!content) continue;

    push({
      id: wish.id,
      kind: "wish",
      title: content.title,
      actorId: wish.addedBy,
      created: wish.createdAt,
      updated: wish.updatedAt,
      href: `/wishes?wish=${wish.id}`,
    });
  }

  for (const message of Object.values(state.messages)) {
    const content = read(message.content, messageContentSchema);
    if (!content) continue;

    entries.push({
      id: message.id,
      kind: "message",
      verb: "created",
      title: content.body.slice(0, 120),
      actorId: message.authorId,
      at: message.createdAt,
      href: `/chat?topic=${message.topicId}`,
    });
  }

  for (const event of Object.values(state.events)) {
    const content = read(event.content, eventContentSchema);
    if (!content) continue;

    push({
      id: event.id,
      kind: "event",
      title: content.title,
      actorId: event.createdBy,
      created: event.createdAt,
      updated: event.updatedAt,
      href: `/calendar?event=${event.id}`,
    });
  }

  for (const list of Object.values(state.lists)) {
    const content = read(list.content, listContentSchema);
    if (!content) continue;

    push({
      id: list.id,
      kind: "list",
      title: content.title,
      actorId: list.createdBy,
      created: list.createdAt,
      updated: list.updatedAt,
      href: `/lists?list=${list.id}`,
    });
  }

  return entries.sort((a, b) => (a.at < b.at ? 1 : -1)).slice(0, limit);
}
